import React from "react";
import dayjs from "@helpers/dayjs";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { EditOutlined, ExpandMore } from "@mui/icons-material";
import CreateOrigin from "../createOrigin";
import { ActivityItem, CreateOriginFields } from "../../types";
import Chip from "@atoms/chip";
import { useCancelForm } from "@context/cancel-form-context";

type OriginAccordionProps = {
  item: ActivityItem;
  type: string;
  index: number;
  expandedAccordions: number[];
  toggleAccordion: (id: number) => void;
  isEditing: boolean;
  handleEdit: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number,
  ) => void;
  fields: CreateOriginFields;
  updateFields: (id: number, values: CreateOriginFields) => void;
  editDataMap: Record<number, CreateOriginFields>;
  handleCancelCallback: (id: number) => void;
  handleSave: (id: number) => void;
};

const OriginAccordion: React.FC<OriginAccordionProps> = ({
  item,
  type,
  index,
  expandedAccordions,
  toggleAccordion,
  isEditing,
  handleEdit,
  fields,
  updateFields,
  handleCancelCallback,
  handleSave,
}) => {
  const { isDirty, hidePrompt, showPrompt, setIsDirty } = useCancelForm();

  const getStatus = () => {
    if (item.completed) return "completed";
    if (!item.completed && !!item.execution_date) return "pending";
    return "To Do";
  };
  const status = getStatus();

  const handleCancel = (id: number) => {
    if (isDirty) {
      showPrompt(
        () => {
          handleCancelCallback(id);
          setIsDirty(false);
          hidePrompt(type);
        },
        () => {
          hidePrompt(type);
        },
        type,
      );
    } else {
      handleCancelCallback(id);
      setIsDirty(false);
    }
  };
  return (
    <Accordion
      key={item.id}
      expanded={expandedAccordions.includes(item.id)}
      onChange={() => toggleAccordion(item.id)}
      sx={{
        boxShadow: "none",
        p: 1,
        backgroundColor: "rgba(250, 250, 250, 1)",
      }}
      className="scInsurance"
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ height: "50px" }}>
        <Box sx={{ width: "calc(100% - 50px)" }}>
          <Typography sx={{ fontSize: "16px" }}>
            #{index + 1} {item.description}
          </Typography>
        </Box>
        {!isEditing && (
          <IconButton onClick={(e) => handleEdit(e, item.id)}>
            <EditOutlined />
          </IconButton>
        )}
      </AccordionSummary>
      <AccordionDetails>
        {!isEditing ? (
          <Stack spacing={1}>
            <Paper sx={{ display: "flex", padding: "8px 16px" }}>
              <Box sx={{ width: "30%" }}>
                <Typography>Status</Typography>
              </Box>
              <Box sx={{ width: "70%" }}>
                <Typography color="text.secondary">
                  <Chip status={status} />
                </Typography>
              </Box>
            </Paper>
            <Paper sx={{ display: "flex", padding: "8px 16px" }}>
              <Box sx={{ width: "30%" }}>
                <Typography>Supplier</Typography>
              </Box>
              <Box sx={{ width: "70%" }}>
                <Typography color="text.secondary">
                  {item.supplier?.name || ""}
                </Typography>
              </Box>
            </Paper>
            <Paper sx={{ display: "flex", padding: "8px 16px" }}>
              <Box sx={{ width: "30%" }}>
                <Typography>Starting date</Typography>
              </Box>
              <Box sx={{ width: "70%" }}>
                <Typography color="text.secondary">
                  {item.execution_date
                    ? dayjs
                        .utc(item.execution_date)
                        .tz(dayjs.tz.guess())
                        .format("DD/MM/YYYY")
                    : ""}
                </Typography>
              </Box>
            </Paper>
            <Paper sx={{ display: "flex", padding: "8px 16px" }}>
              <Box sx={{ width: "30%" }}>
                <Typography>Completion date</Typography>
              </Box>
              <Box sx={{ width: "70%" }}>
                <Typography color="text.secondary">
                  {item.completion_date
                    ? dayjs
                        .utc(item.completion_date)
                        .tz(dayjs.tz.guess())
                        .format("DD/MM/YYYY")
                    : ""}
                </Typography>
              </Box>
            </Paper>
            <Paper sx={{ padding: "8px 16px" }}>
              <Typography mb={0.5}>Note</Typography>
              <Typography color="text.secondary">
                {item.comment || ""}
              </Typography>
            </Paper>
          </Stack>
        ) : (
          <>
            <CreateOrigin
              setIsDirty={setIsDirty}
              fields={fields}
              updateFieldsCallback={(values) => updateFields(item.id, values)}
            />
            <Box display="flex" justifyContent="flex-end" gap={1}>
              {item.supplier && (
                <Button
                  variant="outlined"
                  onClick={() => handleCancel(item.id)}
                >
                  Cancel
                </Button>
              )}
              <Button
                disabled={!fields?.supplier_id}
                variant="contained"
                onClick={() => handleSave(item.id)}
              >
                Save
              </Button>
            </Box>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default OriginAccordion;
